import { Field } from "formik"
import { keyBy, range } from "lodash"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { getDepth } from "react-sortable-tree"
import FieldDropdown from "../../../../../components/fields/FieldDropdown"
import FieldRange from "../../../../../components/fields/FieldRange"
import FieldRichText from "../../../../../components/fields/FieldRichText"
import FieldSlider from "../../../../../components/fields/FieldSlider"
import FieldSwitch from "../../../../../components/fields/FieldSwitch"
import {
  BezierConnection,
  LinearConnection,
  StepConnection,
} from "../../../EstimateVisualization/EstimateTreeViz/Connections"
import EditDataFrame, {
  EditDataSection,
} from "../../EditDataFrame/EditDataFrame"

const EditDataForm = ({
  fullEstimate,
  frag,
  save,
  remove,
  toggle,
  scrollFragIntoView,
}) => {
  const depth = getDepth({ children: fullEstimate.task_tree })

  const { t } = useTranslation(["translation"])
  const ctx = useMemo(() => ({ estimate: fullEstimate }), [fullEstimate])

  const readOnly = fullEstimate.readonly

  const sliderMarks = useMemo(() => {
    const values = range(1, depth + 1).map((v) => {
      if (v < depth) {
        return {
          id: v,
          label: t("tasks.level", { level: v }),
        }
      }
      return {
        id: v,
        label: t("tasks.all"),
      }
    })
    return keyBy(values, "id")
  }, [depth, t])

  const rangeMarks = useMemo(() => {
    const height = fullEstimate.task_tree.length
    const values = range(1, height + 1).map((v) => {
      return {
        id: v,
        label: v,
      }
    })
    return keyBy(values, "id")
  }, [fullEstimate.task_tree.length])

  const orientationOptions = useMemo(
    () => [
      {
        label: t("enums:orientation.horizontal"),
        value: "horizontal",
      },
      {
        label: t("enums:orientation.vertical"),
        value: "vertical",
      },
    ],
    [t]
  )

  const linkTypeOptions = useMemo(
    () => [
      {
        label: <BezierConnection width="19px" height="19px" />,
        value: "diagonal",
      },
      {
        label: <StepConnection width="19px" height="19px" />,
        value: "step",
      },
      {
        label: <LinearConnection width="19px" height="19px" />,
        value: "line",
      },
    ],
    []
  )

  return (
    <EditDataFrame
      contentType={t("documents.frag.viz.dendrogram.title")}
      frag={frag}
      save={save}
      remove={remove}
      toggle={toggle}
      scrollFragIntoView={scrollFragIntoView}
      readOnly={readOnly}
      scenarios={fullEstimate.scenarios}
    >
      {(formik) => (
        <>
          <EditDataSection title={t("field:document.dendrogram.show_caption")}>
            <Field
              name="data.enableCaption"
              message={t("field:document.dendrogram.show_caption_message")}
              component={FieldSwitch}
              readOnly={readOnly}
            />
            {formik.values.data.enableCaption && (
              <div className="mt-4">
                <Field
                  name="data.caption"
                  component={FieldRichText}
                  readOnly={readOnly}
                  placeholder={readOnly ? "" : undefined}
                  enableImages={false}
                  templatingContext={ctx}
                  enableBlockFormatting={false}
                  enableUndoRedo={false}
                  enableFontSize={false}
                  enableLists={false}
                />
              </div>
            )}
          </EditDataSection>
          <EditDataSection title={t("field:document.dendrogram.layout")}>
            <div className="d-flex flex-row justify-content-between align-items-center">
              <label className="m-0">
                {t("field:document.dendrogram.orientation")}
              </label>
              <Field
                name="data.orientation"
                style={{ width: 100 }}
                itemWidth={100}
                options={orientationOptions}
                component={FieldDropdown}
                readOnly={readOnly}
                className="px-2 py-2"
              />
            </div>
            <div className="d-flex flex-row justify-content-between align-items-center mt-4">
              <label className="m-0">
                {t("field:document.dendrogram.link_type")}
              </label>
              <Field
                name="data.link_type"
                style={{ width: 100 }}
                itemWidth={100}
                options={linkTypeOptions}
                component={FieldDropdown}
                readOnly={readOnly}
                className="px-2 py-2"
              />
            </div>
          </EditDataSection>

          <EditDataSection
            title={t("field:document.dendrogram.task_section_title")}
          >
            {fullEstimate.task_tree.length > 1 && (
              <div className="overflow-x-visible">
                <p className="mb-2 text-uppercase">
                  {t("field:document.dendrogram.task_selection")}
                </p>
                <div className="mx-3 overflow-x-visible">
                  <Field
                    name="data.visibleTasks"
                    marks={rangeMarks}
                    component={FieldRange}
                    readOnly={readOnly}
                    style={{ width: "100%" }}
                    className="mb-5"
                  />
                </div>
              </div>
            )}
            {depth > 1 && (
              <div className="mt-4 overflow-x-visible">
                <p className="mb-2 text-uppercase">
                  {t("field:document.dendrogram.depth_level")}
                </p>
                <div className="mx-3 overflow-x-visible">
                  <Field
                    name="data.maxDepth"
                    marks={sliderMarks}
                    max={depth}
                    component={FieldSlider}
                    readOnly={readOnly}
                    style={{ width: "100%" }}
                    className="mb-5"
                  />
                </div>
              </div>
            )}
          </EditDataSection>
        </>
      )}
    </EditDataFrame>
  )
}

export default EditDataForm
